import React, { useRef, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Upload, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable image uploader — returns WebP-compressed public URLs via /api/upload/image.
 * Props:
 *  - folder: destination folder (string)
 *  - label: button text
 *  - multiple: allow selecting multiple files (default false)
 *  - concurrency: parallel uploads when multiple=true (default 4)
 *  - onUploaded(url): called for each successful file (compatibility single-file)
 *  - onUploadedMany(urls): called once with full array at the end (multiple mode)
 */
export default function ImageUploader({
  folder = "misc",
  onUploaded,
  onUploadedMany,
  label = "Uploader une image",
  multiple = false,
  concurrency = 4,
}) {
  const [loading, setLoading] = useState(false);
  const [batch, setBatch] = useState(null); // { total, done, errors, currentName }
  const [lastStats, setLastStats] = useState(null);
  const ref = useRef(null);

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", folder);
    const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data;
  };

  const handlePickSingle = async (file) => {
    try {
      const data = await uploadOne(file);
      onUploaded?.(data.url);
      const ratio = data.original_size > 0 ? Math.round(data.original_size / data.size_bytes) : 1;
      setLastStats({ url: data.url, kb: Math.round(data.size_bytes / 1024), ratio, w: data.width, h: data.height });
      toast.success(`Image compressée × ${ratio} (${Math.round(data.size_bytes / 1024)} Ko)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'upload");
    }
  };

  const handlePickBatch = async (files) => {
    const list = Array.from(files);
    const total = list.length;
    setBatch({ total, done: 0, errors: 0, currentName: "" });
    const successes = [];
    let cursor = 0;

    const worker = async () => {
      while (cursor < list.length) {
        const i = cursor++;
        const f = list[i];
        setBatch((b) => b ? { ...b, currentName: f.name } : b);
        try {
          const data = await uploadOne(f);
          successes.push(data.url);
          onUploaded?.(data.url); // append-on-the-fly
          setBatch((b) => b ? { ...b, done: b.done + 1 } : b);
        } catch {
          setBatch((b) => b ? { ...b, done: b.done + 1, errors: b.errors + 1 } : b);
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(concurrency, total) }).map(() => worker()));
    onUploadedMany?.(successes);
    toast.success(`${successes.length}/${total} image(s) uploadée(s)`);
    // Keep status briefly then clear
    setTimeout(() => setBatch(null), 2500);
  };

  const handlePick = async (e) => {
    const files = e.target.files;
    if (!files || !files.length) return;
    setLoading(true);
    try {
      if (multiple && files.length > 1) await handlePickBatch(files);
      else await handlePickSingle(files[0]);
    } finally {
      setLoading(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <div>
      <input
        ref={ref}
        type="file"
        accept="image/*"
        multiple={multiple}
        className="hidden"
        onChange={handlePick}
        data-testid="img-upload-input"
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => ref.current?.click()}
        disabled={loading}
        className="h-9 gap-1.5"
        data-testid="img-upload-btn"
      >
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
        {loading
          ? (batch ? `${batch.done}/${batch.total}` : "Upload...")
          : label}
      </Button>
      {batch && (
        <div className="mt-1.5 w-56">
          <div className="h-1 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
            <div className="h-full bg-[color:var(--brand-sky)] transition-all" style={{ width: `${(batch.done / batch.total) * 100}%` }} />
          </div>
          <div className="mt-0.5 text-[10px] text-[hsl(var(--muted-foreground))] truncate">
            {batch.currentName || "…"} {batch.errors > 0 && <span className="text-red-500">· {batch.errors} erreur(s)</span>}
          </div>
        </div>
      )}
      {!batch && lastStats && (
        <div className="mt-1.5 text-[11px] text-emerald-700 flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" />
          {lastStats.w}×{lastStats.h} · {lastStats.kb} Ko · WebP × {lastStats.ratio}
        </div>
      )}
    </div>
  );
}
